// src/components/BulkEditorModal.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NOTE_OPTIONS, OCTAVE_OPTIONS } from '../data/musicData';

const NOTE_KEY_MAP = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARP_KEY_MAP = { C: 1, D: 3, F: 6, G: 8, A: 10 };
// Flats map to the same semitone as the enharmonic sharp: Db=C#, Eb=D#, Gb=F#, Ab=G#, Bb=A#.
// Cb and Fb are intentionally omitted — both are rare in practice and would require octave/letter gymnastics.
const FLAT_KEY_MAP = { D: 1, E: 3, G: 6, A: 8, B: 10 };
const MIN_OCTAVE = OCTAVE_OPTIONS[0].value;
const MAX_OCTAVE = OCTAVE_OPTIONS[OCTAVE_OPTIONS.length - 1].value;

const NOTE_LABEL_BY_VALUE = NOTE_OPTIONS.reduce((acc, opt) => {
  if (opt.value !== null) acc[opt.value] = opt.label.split('/')[0];
  return acc;
}, {});

function formatPitch(note, octave) {
  if (note === null || note === undefined) return '—';
  const label = NOTE_LABEL_BY_VALUE[note] ?? '?';
  return `${label}${octave}`;
}

function BulkEditorModal({ isOpen, onClose, onApplyChanges, onApplyPitches, measures }) {
  const [activeTab, setActiveTab] = useState('measures'); // 'measures' | 'pitches'

  const [sections, setSections] = useState([
    { startMeasure: 1, endMeasure: 8, timeSignature: "4/4", grouping: null, removeNonAccents: false }
  ]);
  const [error, setError] = useState("");

  // Pitches tab state
  const [pitchEdits, setPitchEdits] = useState({}); // { "m-b": { dronePitchNote, dronePitchOctave } }
  const [focused, setFocused] = useState({ m: 0, b: 0 });
  const [stickyOctave, setStickyOctave] = useState(4);
  const cellRefs = useRef({});

  // New state for multi-selection
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedSections, setSelectedSections] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Convert current measures to sections when modal opens
  useEffect(() => {
    if (isOpen) {
      if (measures && Array.isArray(measures) && measures.length > 0) {
        // Convert measures array to sections data
        const newSections = convertMeasuresToSections(measures);
        setSections(newSections);
      } else {
        // Fall back to default if measures is invalid
        setSections([{ startMeasure: 1, endMeasure: 8, timeSignature: "4/4", grouping: null, removeNonAccents: false }]);
      }
      setError("");
      setIsSelectionMode(false);
      setSelectedSections(new Set());
      setActiveTab('measures');
      setPitchEdits({});
      setFocused({ m: 0, b: 0 });
      setStickyOctave(4);
    }
  }, [isOpen, measures]);

  // Helpers for the Pitches tab
  const getBeatPitch = useCallback((m, b) => {
    const key = `${m}-${b}`;
    if (pitchEdits[key]) return pitchEdits[key];
    const beat = measures?.[m]?.beatSounds?.[b];
    return {
      dronePitchNote: beat?.dronePitchNote ?? null,
      dronePitchOctave: beat?.dronePitchOctave ?? 4,
    };
  }, [pitchEdits, measures]);

  const setBeatPitchEdit = useCallback((m, b, note, octave) => {
    setPitchEdits((prev) => ({
      ...prev,
      [`${m}-${b}`]: { dronePitchNote: note, dronePitchOctave: octave },
    }));
  }, []);

  const advanceFocused = useCallback(() => {
    setFocused((prev) => {
      const measure = measures?.[prev.m];
      if (!measure) return prev;
      const nextB = prev.b + 1;
      if (nextB < (measure.beatSounds?.length || 0)) return { m: prev.m, b: nextB };
      const nextM = prev.m + 1;
      if (nextM < measures.length) return { m: nextM, b: 0 };
      return prev;
    });
  }, [measures]);

  const retreatFocused = useCallback(() => {
    setFocused((prev) => {
      if (prev.b > 0) return { m: prev.m, b: prev.b - 1 };
      if (prev.m > 0) {
        const prevMeasure = measures?.[prev.m - 1];
        const lastB = (prevMeasure?.beatSounds?.length || 1) - 1;
        return { m: prev.m - 1, b: Math.max(0, lastB) };
      }
      return prev;
    });
  }, [measures]);

  // Scroll focused cell into view when Pitches tab is active
  useEffect(() => {
    if (!isOpen || activeTab !== 'pitches') return;
    const el = cellRefs.current[`${focused.m}-${focused.b}`];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [focused, activeTab, isOpen]);

  // Keyboard handler for Pitches tab
  useEffect(() => {
    if (!isOpen || activeTab !== 'pitches') return;
    if (!measures || measures.length === 0) return;

    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const { m, b } = focused;
      if (!measures[m] || !measures[m].beatSounds?.[b]) return;

      // Detect note letter. With Alt/Option held, e.key on macOS becomes an accented character
      // ("ç" for Option+C etc.), so fall back to the physical-key code "KeyA"–"KeyG".
      let letter = e.key.length === 1 ? e.key.toUpperCase() : '';
      if (!/^[A-G]$/.test(letter) && /^Key[A-G]$/.test(e.code || '')) {
        letter = e.code.slice(3);
      }

      if (/^[A-G]$/.test(letter)) {
        // Alt/Option = flat
        if (e.altKey) {
          e.preventDefault(); // swallow even if no mapping, so the browser never inserts accented chars
          if (Object.prototype.hasOwnProperty.call(FLAT_KEY_MAP, letter)) {
            setBeatPitchEdit(m, b, FLAT_KEY_MAP[letter], stickyOctave);
            advanceFocused();
          }
          return;
        }
        // Shift = sharp
        if (e.shiftKey) {
          if (Object.prototype.hasOwnProperty.call(SHARP_KEY_MAP, letter)) {
            e.preventDefault();
            setBeatPitchEdit(m, b, SHARP_KEY_MAP[letter], stickyOctave);
            advanceFocused();
          }
          return;
        }
        // Plain letter = natural (ignore if Ctrl/Cmd is held — leave browser shortcuts alone)
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          setBeatPitchEdit(m, b, NOTE_KEY_MAP[letter], stickyOctave);
          advanceFocused();
          return;
        }
      }

      // Octave numbers
      if (/^[2-6]$/.test(e.key)) {
        e.preventDefault();
        const oct = Number(e.key);
        setStickyOctave(oct);
        const cur = getBeatPitch(m, b);
        if (cur.dronePitchNote !== null) {
          setBeatPitchEdit(m, b, cur.dronePitchNote, oct);
        }
        return;
      }

      // Clear focused beat
      if (e.key === '0' || e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        setBeatPitchEdit(m, b, null, stickyOctave);
        return;
      }

      // Navigation
      if (e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey)) {
        e.preventDefault();
        advanceFocused();
        return;
      }
      if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
        e.preventDefault();
        retreatFocused();
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocused((prev) => {
          if (prev.m > 0) {
            const target = measures[prev.m - 1];
            const lastB = (target.beatSounds?.length || 1) - 1;
            return { m: prev.m - 1, b: Math.min(prev.b, lastB) };
          }
          return prev;
        });
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocused((prev) => {
          if (prev.m < measures.length - 1) {
            const target = measures[prev.m + 1];
            const lastB = (target.beatSounds?.length || 1) - 1;
            return { m: prev.m + 1, b: Math.min(prev.b, lastB) };
          }
          return prev;
        });
        return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    isOpen,
    activeTab,
    focused,
    stickyOctave,
    measures,
    getBeatPitch,
    setBeatPitchEdit,
    advanceFocused,
    retreatFocused,
  ]);

  const handleApplyPitchChanges = () => {
    if (!onApplyPitches) return;
    const edits = Object.entries(pitchEdits).map(([key, val]) => {
      const [m, b] = key.split('-').map(Number);
      return { measureIdx: m, beatIdx: b, ...val };
    });
    onApplyPitches(edits);
  };

  const clearPitchEdits = () => {
    setPitchEdits({});
  };

  // Function to convert measures array to sections format
  const convertMeasuresToSections = (measuresArray) => {
    const sections = [];
    let currentSection = null;

    const getMeasureGrouping = (m) => {
      if (m.numerator === 5 && [4, 8, 16, 32].includes(m.denominator)) {
        if (m.g2plus3) return "2+3";
        if (m.g3plus2) return "3+2";
      }
      if (m.numerator === 7 && [4, 8, 16, 32].includes(m.denominator)) {
        if (m.g2p2p3) return "2+2+3";
        if (m.g3p2p2) return "3+2+2";
        if (m.g2p3p2) return "2+3+2";
      }
      return null;
    };

    measuresArray.forEach((measure, index) => {
      const measureNumber = index + 1;
      const timeSignature = `${measure.numerator}/${measure.denominator}`;
      const thisGrouping = getMeasureGrouping(measure);
      const thisRemove = !!measure.removeNonAccents;

      if (!currentSection || currentSection.timeSignature !== timeSignature) {
        if (currentSection) {
          sections.push(currentSection);
        }

        currentSection = {
          startMeasure: measureNumber,
          endMeasure: measureNumber,
          timeSignature,
          grouping: thisGrouping,
          // only set removeNonAccents true when all in the section are true; start with current
          removeNonAccents: thisRemove,
          // internal trackers to decide consistency
          _groupingMixed: false,
        };
      } else {
        // Extend the current section
        currentSection.endMeasure = measureNumber;

        // Track grouping consistency: if any mismatch, mark mixed
        if (currentSection.grouping === null) {
          if (thisGrouping !== null) currentSection._groupingMixed = true;
        } else if (thisGrouping !== currentSection.grouping) {
          currentSection._groupingMixed = true;
        }

        // RemoveNonAccents must be true for all to keep it true
        currentSection.removeNonAccents = currentSection.removeNonAccents && thisRemove;
      }
    });

    if (currentSection) {
      if (currentSection._groupingMixed) {
        currentSection.grouping = null; // mixed => don't prefill
      }
      delete currentSection._groupingMixed;
      sections.push(currentSection);
    }

    return sections.length
      ? sections
      : [{ startMeasure: 1, endMeasure: 8, timeSignature: "4/4", grouping: null, removeNonAccents: false }];
  };

  const parseTS = (ts) => {
    const [n, d] = (ts || "").split("/").map((x) => parseInt(x, 10));
    return { n, d };
  };
  const isFiveGroupable = (n, d) => n === 5 && [4, 8, 16, 32].includes(d);
  const isSevenGroupable = (n, d) => n === 7 && [4, 8, 16, 32].includes(d);
  const isCompound = (n, d) => [3, 6, 9, 12, 15].includes(n) && d === 8;

  if (!isOpen) return null;

  const addSection = () => {
    const lastSection = sections[sections.length - 1];
    const newStartMeasure = lastSection.endMeasure + 1;
    setSections([...sections, {
      startMeasure: newStartMeasure,
      endMeasure: newStartMeasure + 7,
      timeSignature: lastSection.timeSignature,
      grouping: null,
      removeNonAccents: false
    }]);
  };

  const removeSection = (index) => {
    if (sections.length > 1) {
      setSections(sections.filter((_, i) => i !== index));
    }
  };

  const duplicateSection = (index) => {
    const sectionToDuplicate = sections[index];
    const newStartMeasure = sections[sections.length - 1].endMeasure + 1;
    const length = sectionToDuplicate.endMeasure - sectionToDuplicate.startMeasure + 1;
    
    setSections([...sections, {
      startMeasure: newStartMeasure,
      endMeasure: newStartMeasure + length - 1,
      timeSignature: sectionToDuplicate.timeSignature,
      grouping: sectionToDuplicate.grouping ?? null,
      removeNonAccents: !!sectionToDuplicate.removeNonAccents
    }]);
  };

  const updateSection = (index, field, value) => {
    const updatedSections = [...sections];
    
    // Convert to number for measure fields, but allow empty strings
    if (field === 'startMeasure' || field === 'endMeasure') {
      value = value === "" ? "" : (parseInt(value) || 0);
    }
    
    const updated = { ...updatedSections[index], [field]: value };

    // If time signature changed, adjust grouping availability
    if (field === 'timeSignature') {
      const { n, d } = parseTS(value);
      if (!(isFiveGroupable(n, d) || isSevenGroupable(n, d) || isCompound(n, d))) {
        updated.grouping = null;
        updated.removeNonAccents = false;
      }
    }

    updatedSections[index] = updated;
    
    // Ensure end measure is not less than start measure
    if (field === 'startMeasure' && updatedSections[index].endMeasure < value) {
      updatedSections[index].endMeasure = value;
    }
    
    setSections(updatedSections);
  };

  const validateSections = () => {
    // Sort sections by start measure
    const sortedSections = [...sections].sort((a, b) => a.startMeasure - b.startMeasure);
    
    // Check for overlaps or invalid ranges
    for (let i = 0; i < sortedSections.length; i++) {
      const section = sortedSections[i];
      
      // Check for valid range
      if (section.startMeasure > section.endMeasure) {
        setError(`Section ${i + 1} has invalid range: start measure must be less than or equal to end measure`);
        return false;
      }
      
      // Check for overlap with next section
      if (i < sortedSections.length - 1) {
        if (section.endMeasure >= sortedSections[i + 1].startMeasure) {
          setError(`Sections ${i + 1} and ${i + 2} overlap`);
          return false;
        }
      }
    }
    
    setError("");
    return true;
  };

  const handleApplyChanges = () => {
    if (validateSections()) {
      // Format sections for the parent component
      const formattedInput = sections.map(section => {
        const range = section.startMeasure === section.endMeasure
          ? `${section.startMeasure}`
          : `${section.startMeasure}-${section.endMeasure}`;

        // Append options as " | group=...; removeNonAccents"
        const opts = [];
        if (section.grouping) opts.push(`group=${section.grouping}`);
        if (section.removeNonAccents) opts.push(`removeNonAccents`);
        const optStr = opts.length ? ` | ${opts.join('; ')}` : '';

        return `${range} => ${section.timeSignature}${optStr}`;
      }).join('\n');
      
      onApplyChanges(formattedInput);
    }
  };

  const handleKeyDown = (e, index) => {
    // Add new row on Tab at the last cell of the last row
    if (e.key === 'Tab' && !e.shiftKey && index === sections.length - 1 && e.target.name === "timeSignature") {
      e.preventDefault();
      addSection();
    }
  };

  // Multi-selection functions
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedSections(new Set());
  };

  const toggleSectionSelection = (index) => {
    const newSelected = new Set(selectedSections);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSections(newSelected);
  };

  const selectAllSections = () => {
    setSelectedSections(new Set(sections.map((_, index) => index)));
  };

  const clearSelection = () => {
    setSelectedSections(new Set());
  };

  const copySelectedSections = () => {
    if (selectedSections.size === 0) return;
    
    const lastMeasure = Math.max(...sections.map(s => s.endMeasure));
    let nextStartMeasure = lastMeasure + 1;
    
    const newSections = [...sections];
    
    // Sort selected indices to maintain order
    const sortedIndices = Array.from(selectedSections).sort((a, b) => a - b);
    
    sortedIndices.forEach(index => {
      const sectionToCopy = sections[index];
      const length = sectionToCopy.endMeasure - sectionToCopy.startMeasure + 1;
      
      newSections.push({
        startMeasure: nextStartMeasure,
        endMeasure: nextStartMeasure + length - 1,
        timeSignature: sectionToCopy.timeSignature,
        grouping: sectionToCopy.grouping ?? null,
        removeNonAccents: !!sectionToCopy.removeNonAccents
      });
      
      nextStartMeasure += length;
    });
    
    setSections(newSections);
    setSelectedSections(new Set());
    setIsSelectionMode(false);
    
    // Haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const deleteSelectedSections = () => {
    if (selectedSections.size === 0 || selectedSections.size === sections.length) return;
    
    const newSections = sections.filter((_, index) => !selectedSections.has(index));
    setSections(newSections);
    setSelectedSections(new Set());
    setIsSelectionMode(false);
  };

  const RemoveNonAccentsToggle = ({ value, onChange, disabled }) => (
    <label className="flex items-center gap-2 mt-1 leading-tight">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="w-4 h-4"
      />
      <span className="text-[11px] sm:text-sm text-gray-700 leading-tight">
        <span className="inline sm:hidden">No non-accents</span>
        <span className="hidden sm:inline">Remove non-accented beats</span>
      </span>
    </label>
  );

  return (
    <div className="modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="modal-content bg-white rounded-lg p-4 w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Bulk Editor</h2>
          {/* Multi-select button only for the Measures tab */}
          {activeTab === 'measures' && (
            <button
              onClick={toggleSelectionMode}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                isSelectionMode
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {isSelectionMode ? 'Exit Select' : 'Multi-Select'}
            </button>
          )}
        </div>

        {/* Tab switcher */}
        <div className="inline-flex bg-gray-200 rounded-full p-1 mb-4">
          <button
            onClick={() => setActiveTab('measures')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeTab === 'measures'
                ? 'bg-accent-700 text-white shadow-sm'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Measures
          </button>
          <button
            onClick={() => setActiveTab('pitches')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeTab === 'pitches'
                ? 'bg-accent-700 text-white shadow-sm'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Pitches
          </button>
        </div>

        {/* Selection mode toolbar */}
        {activeTab === 'measures' && isSelectionMode && (
          <div className="sticky top-0 z-10 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 shadow-sm">
            <div className="flex flex-wrap gap-2 items-center justify-between">
              <div className="text-sm text-blue-800">
                {selectedSections.size} section{selectedSections.size !== 1 ? 's' : ''} selected
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={selectAllSections}
                  className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200"
                >
                  Select All
                </button>
                <button
                  onClick={clearSelection}
                  className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                >
                  Clear
                </button>
                <button
                  onClick={copySelectedSections}
                  disabled={selectedSections.size === 0}
                  className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs hover:bg-green-200 disabled:opacity-50"
                >
                  Copy to End
                </button>
                <button
                  onClick={deleteSelectedSections}
                  disabled={selectedSections.size === 0 || selectedSections.size === sections.length}
                  className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs hover:bg-red-200 disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'measures' && (
        <>
        <p className="mb-2 text-sm text-gray-600">
          {isSelectionMode
            ? "Click sections to select them, then use the toolbar above to copy or delete."
            : "Define your measure sections and time signatures:"
          }
        </p>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse mb-4">
            <thead>
              <tr>
                {isSelectionMode && (
                  <th className="border p-2 bg-gray-100 w-12">
                    <input
                      type="checkbox"
                      checked={selectedSections.size === sections.length && sections.length > 0}
                      onChange={selectedSections.size === sections.length ? clearSelection : selectAllSections}
                      className="w-4 h-4"
                    />
                  </th>
                )}
                <th className="border p-2 bg-gray-100">
                  <span className="hidden sm:inline">First</span>
                  <span className="sm:hidden">1st</span>
                </th>
                <th className="border p-2 bg-gray-100">
                  <span className="hidden sm:inline">Last</span>
                  <span className="sm:hidden">Last</span>
                </th>
                <th className="border p-2 bg-gray-100">
                  <span className="hidden sm:inline">Time Sig</span>
                  <span className="sm:hidden">Sig</span>
                </th>
                <th className="border p-2 bg-gray-100">
                  <span className="hidden sm:inline">Grouping</span>
                  <span className="sm:hidden">Group</span>
                </th>
                {!isSelectionMode && (
                  <th className="border p-2 bg-gray-100">
                    <span className="hidden sm:inline">Actions</span>
                    <span className="sm:hidden">Action</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sections.map((section, index) => (
                <tr 
                  key={index}
                  className={`
                    ${isSelectionMode ? 'cursor-pointer hover:bg-gray-50' : ''}
                    ${selectedSections.has(index) ? 'bg-blue-50 border-blue-300' : ''}
                  `}
                  onClick={isSelectionMode ? () => toggleSectionSelection(index) : undefined}
                >
                  {isSelectionMode && (
                    <td className="border p-2 text-center">
                      <input
                        type="checkbox"
                        checked={selectedSections.has(index)}
                        onChange={() => toggleSectionSelection(index)}
                        className="w-4 h-4"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  <td className="border p-1">
                    <input
                      type="number"
                      name="startMeasure"
                      value={section.startMeasure}
                      onChange={(e) => updateSection(index, 'startMeasure', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className={`w-full p-2 border rounded text-center ${isMobile ? 'text-base' : 'text-sm'}`}
                      min="1"
                      disabled={isSelectionMode}
                      style={{ minHeight: isMobile ? '44px' : 'auto' }}
                    />
                  </td>
                  <td className="border p-1">
                    <input
                      type="number"
                      name="endMeasure"
                      value={section.endMeasure}
                      onChange={(e) => updateSection(index, 'endMeasure', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className={`w-full p-2 border rounded text-center ${isMobile ? 'text-base' : 'text-sm'}`}
                      min={section.startMeasure}
                      disabled={isSelectionMode}
                      style={{ minHeight: isMobile ? '44px' : 'auto' }}
                    />
                  </td>
                  <td className="border p-1">
                    <input
                      type="text"
                      name="timeSignature"
                      value={section.timeSignature}
                      onChange={(e) => updateSection(index, 'timeSignature', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className={`w-full p-2 border rounded text-center ${isMobile ? 'text-base' : 'text-sm'}`}
                      placeholder="4/4"
                      disabled={isSelectionMode}
                      style={{ minHeight: isMobile ? '44px' : 'auto' }}
                    />
                  </td>
                  <td className="border p-1">
                    {(() => {
                      const { n, d } = parseTS(section.timeSignature);

                      // 5/x options
                      if (isFiveGroupable(n, d)) {
                        return (
                          <div className={`flex flex-col gap-1 ${isMobile ? 'text-base' : 'text-sm'}`}>
                            <select
                              value={section.grouping || ""}
                              onChange={(e) => updateSection(index, 'grouping', e.target.value || null)}
                              disabled={isSelectionMode}
                              className="w-full p-2 border rounded"
                              style={{ minHeight: isMobile ? '44px' : 'auto' }}
                            >
                              <option value="">None</option>
                              <option value="2+3">2+3</option>
                              <option value="3+2">3+2</option>
                            </select>

                            {section.grouping && (
                              <RemoveNonAccentsToggle
                                value={section.removeNonAccents}
                                onChange={(val) => updateSection(index, 'removeNonAccents', val)}
                                disabled={isSelectionMode}
                              />
                            )}
                          </div>
                        );
                      }

                      // 7/x options
                      if (isSevenGroupable(n, d)) {
                        return (
                          <div className={`flex flex-col gap-1 ${isMobile ? 'text-base' : 'text-sm'}`}>
                            <select
                              value={section.grouping || ""}
                              onChange={(e) => updateSection(index, 'grouping', e.target.value || null)}
                              disabled={isSelectionMode}
                              className="w-full p-2 border rounded"
                              style={{ minHeight: isMobile ? '44px' : 'auto' }}
                            >
                              <option value="">None</option>
                              <option value="2+2+3">2+2+3</option>
                              <option value="3+2+2">3+2+2</option>
                              <option value="2+3+2">2+3+2</option>
                            </select>

                            {section.grouping && (
                              <RemoveNonAccentsToggle
                                value={section.removeNonAccents}
                                onChange={(val) => updateSection(index, 'removeNonAccents', val)}
                                disabled={isSelectionMode}
                              />
                            )}
                          </div>
                        );
                      }

                      // Compound meters: show only the removal toggle
                      if (isCompound(n, d)) {
                        return (
                          <div className={`flex flex-col gap-1 ${isMobile ? 'text-base' : 'text-sm'}`}>
                            <div className="text-gray-700 text-[11px] sm:text-sm">
                              <span className="hidden sm:inline">Compound accents</span>
                              <span className="sm:hidden">Compound</span>
                            </div>
                            <RemoveNonAccentsToggle
                              value={section.removeNonAccents}
                              onChange={(val) => updateSection(index, 'removeNonAccents', val)}
                              disabled={isSelectionMode}
                            />
                          </div>
                        );
                      }

                      // Not groupable
                      return <div className="text-gray-400 text-xs">—</div>;
                    })()}
                  </td>
                  {!isSelectionMode && (
                    <td className="border p-1">
                      <div className="flex flex-col sm:flex-row gap-1">
                        <button
                          onClick={() => duplicateSection(index)}
                          className={`px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 text-xs ${
                            isMobile ? 'min-h-[36px]' : ''
                          }`}
                          title="Copy this section"
                        >
                          <span className="hidden sm:inline">Copy</span>
                          <span className="sm:hidden">Dup</span>
                        </button>
                        <button
                          onClick={() => removeSection(index)}
                          className={`px-2 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 text-xs ${isMobile ? 'min-h-[36px]' : ''}`}
                          title="Delete this section"
                          disabled={sections.length <= 1}
                        >
                          <span className="hidden sm:inline">Delete</span>
                          <span className="sm:hidden">Del</span>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {error && <div className="text-red-600 mb-4 text-sm">{error}</div>}
        
        {!isSelectionMode && (
          <div className="mb-4">
            <button
              onClick={addSection}
              className={`px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 ${
                isMobile ? 'w-full min-h-[44px]' : ''
              }`}
            >
              Add Section
            </button>
          </div>
        )}

        {/* Help text for all users */}
        <div className="text-xs text-gray-500 mb-4 p-2 bg-gray-50 rounded">
          💡 <strong>Tip:</strong> Use "Multi-Select" to choose multiple sections, then "Copy to End" to duplicate them all at once.
        </div>
        </>
        )}

        {activeTab === 'pitches' && (
          <>
            {(!measures || measures.length === 0) ? (
              <p className="mb-2 text-sm text-gray-600">
                Add some measures first, then come back here to assign pitches.
              </p>
            ) : (
              <>
                <p className="mb-2 text-sm text-gray-600">
                  Click a beat (or use the arrow keys) to focus it, then press a note key to assign a pitch.
                </p>

                <div className="flex flex-wrap items-center justify-between gap-2 mb-3 p-2 bg-gray-50 rounded border border-gray-200">
                  <div className="text-xs sm:text-sm text-gray-700">
                    <span className="font-medium">Current octave:</span>{' '}
                    <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded bg-white border border-gray-300 font-mono">
                      {stickyOctave}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">
                      {Object.keys(pitchEdits).length} pending change{Object.keys(pitchEdits).length === 1 ? '' : 's'}
                    </span>
                    <button
                      type="button"
                      onClick={clearPitchEdits}
                      disabled={Object.keys(pitchEdits).length === 0}
                      className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="max-h-[50vh] overflow-y-auto border border-gray-200 rounded mb-3">
                  <table className="w-full border-collapse">
                    <tbody>
                      {measures.map((measure, mIdx) => {
                        const beats = measure.beatSounds || [];
                        return (
                          <tr key={mIdx} className="border-b last:border-b-0">
                            <td className="p-2 align-top text-xs sm:text-sm text-gray-500 font-medium w-12 bg-gray-50 border-r border-gray-200">
                              M{mIdx + 1}
                            </td>
                            <td className="p-2">
                              <div className="flex flex-wrap gap-1">
                                {beats.map((beat, bIdx) => {
                                  const key = `${mIdx}-${bIdx}`;
                                  const isFocused = focused.m === mIdx && focused.b === bIdx;
                                  const isEdited = !!pitchEdits[key];
                                  const pitch = getBeatPitch(mIdx, bIdx);
                                  const label = formatPitch(pitch.dronePitchNote, pitch.dronePitchOctave);
                                  return (
                                    <button
                                      key={bIdx}
                                      type="button"
                                      ref={(el) => { cellRefs.current[key] = el; }}
                                      onClick={() => setFocused({ m: mIdx, b: bIdx })}
                                      className={`min-w-[44px] px-2 py-1 rounded text-xs sm:text-sm font-mono border transition-colors ${
                                        isFocused
                                          ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50 text-blue-900'
                                          : isEdited
                                            ? 'border-yellow-400 bg-yellow-50 text-yellow-900'
                                            : pitch.dronePitchNote === null
                                              ? 'border-gray-200 bg-white text-gray-400'
                                              : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                                      }`}
                                      title={`Measure ${mIdx + 1}, beat ${bIdx + 1}`}
                                    >
                                      {label}
                                    </button>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="text-xs text-gray-600 mb-4 p-2 bg-gray-50 rounded leading-relaxed">
                  <div className="mb-1"><strong>Keyboard:</strong></div>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li><span className="font-mono">C D E F G A B</span> — assign note (advances to next beat). Hold <span className="font-mono">Shift</span> for sharps, <span className="font-mono">Alt/Option</span> for flats.</li>
                    <li><span className="font-mono">{MIN_OCTAVE}–{MAX_OCTAVE}</span> — set the current octave (and update the focused beat if it has a note).</li>
                    <li><span className="font-mono">← → ↑ ↓</span> or <span className="font-mono">Tab</span> — navigate between beats. Click any cell to jump.</li>
                    <li><span className="font-mono">0</span> or <span className="font-mono">Backspace</span> — clear the focused beat (Off).</li>
                  </ul>
                </div>
              </>
            )}
          </>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="text-xs text-gray-600">
            {activeTab === 'measures' ? (
              <p>Keyboard shortcuts: Tab to navigate{!isMobile && ', Tab at end of last row to add new row'}</p>
            ) : (
              <p>Tip: press a note key to assign and auto-advance. Numbers set the current octave.</p>
            )}
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={activeTab === 'measures' ? handleApplyChanges : handleApplyPitchChanges}
              disabled={activeTab === 'pitches' && Object.keys(pitchEdits).length === 0}
              className={`px-4 py-2 text-sm bg-brand-500 text-white rounded hover:bg-brand-700 disabled:opacity-50 disabled:hover:bg-brand-500 ${
                isMobile ? 'flex-1 min-h-[44px]' : ''
              }`}
            >
              Apply Changes
            </button>
            <button
              onClick={onClose}
              className={`px-4 py-2 text-sm bg-gray-400 text-white rounded hover:bg-gray-500 ${
                isMobile ? 'flex-1 min-h-[44px]' : ''
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkEditorModal;